/**
 * A company's "Sales & Billing" section — ONE section, FOUR tabs, each a compact
 * table of the NEWEST documents raised against the account (up to 100 per kind,
 * the API's page cap), with a "View all N …" link under each to the list page
 * filtered to this company. Owner decision, 2026-10-11. Each name is a real link to its record page that, on a
 * plain click by a reader who may edit it, opens the record's edit form in a
 * modal instead (see the quick-edit hosts in `CompanyDocumentsHistory`).
 *
 * Two exports, split the way the UP dashboard splits its tables:
 *
 *   - `useCompanyDocumentsHistory(companyId)` owns the five reads — the four
 *     document lists and the per-currency credit balance — and exposes
 *     `counts` so the page can print `companyDocumentsSummary(state.counts)` in
 *     the folded section header without mounting the tables.
 *   - `CompanyDocumentsHistory` is presentational: the tab strip, the chosen
 *     table, and the Credit notes tab's balance block above its rows.
 *
 * ## Newest first is the endpoint's own default
 *
 * All four list endpoints default to `id DESC`, which IS newest-first, so no
 * `sort` is sent. Sending `sort: 'id', order: 'desc'` to mean the same thing is
 * what `DealListParams` says not to do.
 *
 * ## Nothing here sums or converts
 *
 * A company routinely holds documents in several currencies. Every figure is
 * rendered beside its own code, straight off the wire, and no column is
 * totalled. The balance block is the server's per-currency figure through the
 * same `CompanyCreditBalance` the standalone panel used — every one of its
 * states (skeleton, No credit, No spendable credit, Couldn't load, the 403
 * refusal split on STATUS) is kept, and its notes list is REPLACED by this
 * tab's table rather than rendered twice.
 *
 * ## `null` count means NOT READ, never zero
 *
 * `counts[kind]` is `null` while a read is in flight or after it failed or was
 * refused. The ordinary case for `null` is a reader without `credit-notes.read`
 * — the three CRM reads are open to every role, that one answers 403 — and the
 * summary omits a `null` rather than printing a `0` the reader was never
 * allowed to verify.
 *
 * ## The table below `md`
 *
 * One scrollable table inside `TableWrap` at every width — no stacked card.
 * Four or five columns at 320px is a horizontal scroll, which the wrapper owns,
 * rather than a second renderer whose empty and failure states would have to be
 * kept in step with this one. The Cycle column and the funnels are therefore
 * the same at every width.
 *
 * ## Cycle column and column filters (owner, 2026-10-16)
 *
 * Every tab leads with a Cycle column. Documents carry no cycle id, so it is
 * DERIVED: the cycles are read once and each row's `cycleDate` (see
 * `CompanyDocumentRow`) is placed with `cycleOfDate`. No date, no matching
 * window, or a failed cycles read all render `—`; the table never breaks on it.
 *
 * Every column but Actions carries the `ColumnFilter` funnel, filtering the
 * LOADED rows client-side: a value list for Cycle, name and Stage/Status, an
 * inclusive range for figures and dates. Filters live in component state, are
 * not remembered, and reset per tab (`key={kind}`).
 *
 * ⚠️ An amount bound compares the figure as typed against each row's own
 * figure, whatever its currency — nothing is converted or summed.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DownloadPdfButton } from '@/components/documents/DownloadPdfButton';
import {
  ConvertQuoteMenu,
  CreateFromDealMenu,
  DEAL_CREATE_TARGETS,
} from '@/components/records/RecordCreateMenu';
import { RaiseCreditNoteButton } from '@/components/credit-notes/RaiseCreditNoteButton';
import { useInvoiceStatusFlags } from '@/components/credit-notes/use-invoice-status-flags';
import { useRaiseCreditNote } from '@/components/credit-notes/use-raise-credit-note';
import { ArrowUpRightIcon, CheckIcon } from '@/components/catalog/icons';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  FilterableTH,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  type ColumnFilterOption,
  type ColumnFilterState,
  type FilterableTHProps,
} from '@/components/ui';
import {
  EMPTY_FILTER,
  hasFilterInput,
  isFilterActive,
  rangeBounds,
  type ColumnRangeSpec,
} from '@/components/ui/ColumnFilter';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { TextLink } from '@/components/ui/TextLink';
import { StatusBadge } from '@/components/invoices/InvoiceBits';
import { InvoiceNumberLink, InvoiceQuickEdit } from '@/components/invoices/InvoiceNumberLink';
import { StageBadge } from '@/components/quotes/QuoteBits';
import { QuoteNumberLink, QuoteQuickEdit } from '@/components/quotes/QuoteNumberLink';
import { PreviewQuotePdfButton, useQuotePdfPreview } from '@/components/quotes/QuotePdfPreview';
import { DealQuotesPdfMenu } from '@/components/quotes/DealQuotesPdfMenu';
import { DealNameLink, DealQuickEdit } from '@/components/deals/DealNameLink';
import { CreditNoteLink, CreditNoteQuickEdit } from '@/components/credit-notes/CreditNoteLink';
import { DealStageBadge } from '@/components/StageSwatch';
import {
  CompanyCreditBalance,
  CreditRefusal,
  creditReadFailureOf,
  type CreditRead,
} from '@/components/credit-notes/CompanyCreditPanel';
import { ApiError, api } from '@/lib/api';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  COMPANY_DOCUMENT_KINDS,
  COMPANY_DOCUMENT_LABELS,
  companyDocumentListHref,
  creditNoteRow,
  dealRow,
  invoiceRow,
  quoteRow,
  viewAllLabel,
  withinBounds,
  type CompanyDocumentKind,
  type CompanyDocumentRow,
} from '@/lib/company-documents';
import { cycleOfDate, type CycleOfDate } from '@/lib/cycle-of-date';
import { documentCustomerName } from '@/lib/company-names';
import {
  CREDIT_NOTE_NOT_SENT_NOTE,
  CREDIT_REMAINING_LABEL,
  approvalConsequence,
  creditNoteGateOf,
  creditNotePushOutcome,
  creditNoteRef,
  creditNoteStatusTone,
  spendableOf,
} from '@/lib/credit-notes';
import { cx } from '@/lib/cx';
import { formatDateOnly, orDash } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type {
  CompanyCreditBalance as CreditBalanceRow,
  CreditNote,
  Cycle,
  Deal,
  Invoice,
  Quote,
} from '@/lib/types';

/**
 * How many of each kind the tab loads before sending the reader to the list
 * page — the API's `perPage` cap, so the column filters act on as much of the
 * account as one read can carry.
 */
const ROWS_PER_KIND = 100;

/** A page of one kind, with the server's total — the count the tab strip shows. */
export interface DocumentPage<T> {
  readonly rows: readonly T[];
  readonly total: number;
}

export interface CompanyDocumentsHistoryState {
  readonly companyId: number;
  readonly deals: CreditRead<DocumentPage<Deal>>;
  readonly quotes: CreditRead<DocumentPage<Quote>>;
  readonly invoices: CreditRead<DocumentPage<Invoice>>;
  readonly creditNotes: CreditRead<DocumentPage<CreditNote>>;
  readonly creditBalances: CreditRead<readonly CreditBalanceRow[]>;
  /** Every cycle, for the derived Cycle column; `null` while loading or after a failure. */
  readonly cycles: readonly Cycle[] | null;
  /** `meta.total` per kind; `null` while not read, failed or refused. */
  readonly counts: Readonly<Record<CompanyDocumentKind, number | null>>;
  /** Re-issues all five reads. The page calls it after a create. */
  readonly reload: () => void;
}

const LOADING: CreditRead<never> = { kind: 'loading' };

function pageOf<T>(response: { data: T[]; meta: { total: number } }): CreditRead<DocumentPage<T>> {
  return { kind: 'ok', data: { rows: response.data, total: response.meta.total } };
}

function countOf<T>(read: CreditRead<DocumentPage<T>>): number | null {
  return read.kind === 'ok' ? read.data.total : null;
}

/**
 * Owns the five reads. Every one passes the one `AbortSignal`, guards on
 * `signal.aborted` in both branches, and resets to `loading` when the company
 * or the reload token changes — so a different company is never shown the
 * previous company's rows under its own name, and a reload after a create
 * draws skeletons rather than a table that is about to change under the reader.
 */
export function useCompanyDocumentsHistory(companyId: number): CompanyDocumentsHistoryState {
  const [deals, setDeals] = useState<CreditRead<DocumentPage<Deal>>>(LOADING);
  const [quotes, setQuotes] = useState<CreditRead<DocumentPage<Quote>>>(LOADING);
  const [invoices, setInvoices] = useState<CreditRead<DocumentPage<Invoice>>>(LOADING);
  const [creditNotes, setCreditNotes] = useState<CreditRead<DocumentPage<CreditNote>>>(LOADING);
  const [creditBalances, setCreditBalances] =
    useState<CreditRead<readonly CreditBalanceRow[]>>(LOADING);
  const [reloadToken, setReloadToken] = useState(0);
  const [cycles, setCycles] = useState<readonly Cycle[] | null>(null);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  /*
   * The cycles, read ONCE per mount — they do not change with the company or a
   * create. A failure leaves `null`, which renders every Cycle cell as `—`
   * rather than refusing the tables.
   */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listCycles({ perPage: 100 }, signal)
      .then((response) => {
        if (signal.aborted) return;
        setCycles(response.data);
      })
      .catch(() => {
        if (signal.aborted) return;
        setCycles(null);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setDeals(LOADING);
    setQuotes(LOADING);
    setInvoices(LOADING);
    setCreditNotes(LOADING);
    setCreditBalances(LOADING);

    const params = { companyId, perPage: ROWS_PER_KIND };

    api
      .listDeals(params, signal)
      .then((response) => {
        if (signal.aborted) return;
        setDeals(pageOf(response));
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setDeals(creditReadFailureOf(caught));
      });

    api
      .listQuotes(params, signal)
      .then((response) => {
        if (signal.aborted) return;
        setQuotes(pageOf(response));
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setQuotes(creditReadFailureOf(caught));
      });

    api
      .listInvoices(params, signal)
      .then((response) => {
        if (signal.aborted) return;
        setInvoices(pageOf(response));
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setInvoices(creditReadFailureOf(caught));
      });

    api
      .listCreditNotes(params, signal)
      .then((response) => {
        if (signal.aborted) return;
        setCreditNotes(pageOf(response));
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setCreditNotes(creditReadFailureOf(caught));
      });

    api
      .getCompanyCreditBalances(companyId, signal)
      .then((response) => {
        if (signal.aborted) return;
        setCreditBalances({ kind: 'ok', data: response.data });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setCreditBalances(creditReadFailureOf(caught));
      });

    return () => controller.abort();
  }, [companyId, reloadToken]);

  return {
    companyId,
    deals,
    quotes,
    invoices,
    creditNotes,
    creditBalances,
    cycles,
    counts: {
      deals: countOf(deals),
      quotes: countOf(quotes),
      invoices: countOf(invoices),
      creditNotes: countOf(creditNotes),
    },
    reload,
  };
}

/* --- credit-note row actions ---------------------------------------------- */

type PendingCreditNoteAction = { kind: 'submit' | 'approve'; note: CreditNote };

export interface CreditNoteRowActions {
  /** The row's Submit / Approve icon buttons; nothing when neither applies. */
  controls: (note: CreditNote) => ReactNode;
  /** The ONE confirmation dialog — mount once per list, never per row. */
  dialogs: ReactNode;
  /** The last approve's Xero outcome when it was not `pushed`; render above the list. */
  notice: ReactNode;
}

/**
 * "Submit for approval" and "Approve" on a credit-note LIST row (owner,
 * 2026-10-28: a Draft row on the company page's Credit notes tab had an empty
 * Actions cell, so an Accounts user could not move it on). Shared by this
 * section and `/credit-notes`; one copy of the gates, the wording and the
 * refusal handling. The detail page keeps its own (it also reloads allocations).
 *
 * Gates are the detail page's, on the status FLAGS, never the names:
 *   - Submit: `status.isEditable && !status.isVoid && canUpdate('credit-notes')`.
 *   - Approve: `!status.isApproved && !status.isVoid && canApprove('credit-notes')`
 *     — a separate grant Accounts does not hold. The server's 403 stays the guarantee.
 *
 * Both dialogs say `CREDIT_NOTE_NOT_SENT_NOTE`: neither action emails the customer.
 * `onChanged` re-reads the list after a success.
 */
export function useCreditNoteRowActions(onChanged: () => void): CreditNoteRowActions {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('credit-notes');
  const canApprove = ability.canApprove('credit-notes');
  const [pending, setPending] = useState<PendingCreditNoteAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ tone: 'neutral' | 'danger'; text: string } | null>(
    null,
  );

  const close = () => {
    if (busy) return;
    setPending(null);
    setError(null);
  };

  const confirm = async () => {
    if (pending === null) return;
    const { kind, note } = pending;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'submit') {
        await api.submitCreditNoteForApproval(note.id);
        setOutcome(null);
      } else {
        const response = await api.approveCreditNote(note.id);
        // `?.` — an older server answers `{data}` alone. A pushed note needs no banner.
        const push = response.xeroPush ? creditNotePushOutcome(response.xeroPush, 'approve') : null;
        setOutcome(
          push !== null && push.tone !== 'success'
            ? { tone: push.tone === 'danger' ? 'danger' : 'neutral', text: `${creditNoteRef(note)}: ${push.text}` }
            : null,
        );
      }
      setPending(null);
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setError(
          denialReason(ability, kind === 'approve' ? 'approve' : 'update', 'credit-notes') ??
            (kind === 'approve'
              ? 'Your access changed since this page loaded, so the credit note was not approved.'
              : 'Your access changed since this page loaded, so the credit note was not submitted.'),
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        setError('The credit note was not found. It may have been deleted. Nothing changed.');
      } else {
        setError(caught instanceof Error ? caught.message : 'That didn’t work.');
      }
    } finally {
      setBusy(false);
    }
  };

  const controls = (note: CreditNote): ReactNode => {
    const { status } = note;
    const showSubmit = status.isEditable && !status.isVoid && canUpdate;
    const showApprove = !status.isApproved && !status.isVoid && canApprove;
    const ref = creditNoteRef(note);
    return (
      <>
        {showSubmit && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            leadingIcon={<ArrowUpRightIcon />}
            aria-label={`Submit ${ref} for approval`}
            tooltip="Submit for approval"
            onClick={() => {
              setError(null);
              setPending({ kind: 'submit', note });
            }}
          />
        )}
        {showApprove && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            leadingIcon={<CheckIcon />}
            aria-label={`Approve ${ref}`}
            tooltip="Approve"
            onClick={() => {
              setError(null);
              setPending({ kind: 'approve', note });
            }}
          />
        )}
      </>
    );
  };

  const ref = pending ? creditNoteRef(pending.note) : '';
  const dialogs = (
    <ConfirmDialog
      open={pending !== null}
      onClose={close}
      onConfirm={() => void confirm()}
      title={pending?.kind === 'approve' ? `Approve ${ref}?` : `Submit ${ref} for approval?`}
      description={
        pending === null ? null : pending.kind === 'approve' ? (
          <p className="m-0">
            {approvalConsequence(pending.note)} {CREDIT_NOTE_NOT_SENT_NOTE}
          </p>
        ) : (
          <p className="m-0">
            {ref} goes to the approvers. It can no longer be edited until it is approved or
            voided. {CREDIT_NOTE_NOT_SENT_NOTE}
          </p>
        )
      }
      confirmLabel={pending?.kind === 'approve' ? 'Approve credit note' : 'Submit for approval'}
      tone="primary"
      loading={busy}
      error={error}
    />
  );

  const notice =
    outcome === null ? null : (
      <Notice
        tone={outcome.tone === 'danger' ? 'danger' : 'warning'}
        role="status"
        action={
          <Button variant="ghost" size="sm" onClick={() => setOutcome(null)}>
            Dismiss
          </Button>
        }
      >
        <span className="[overflow-wrap:anywhere]">{outcome.text}</span>
      </Notice>
    );

  return { controls, dialogs, notice };
}

/* --- the section ---------------------------------------------------------- */

/** A remembered tab: one of the four kinds, or `null` (follow the data). */
function parseDocumentKind(raw: unknown): CompanyDocumentKind | null | undefined {
  if (raw === null) return null;
  return COMPANY_DOCUMENT_KINDS.find((kind) => kind === raw);
}

/** A remembered funnel set: a plain object of filter states, nothing else. */
function parseFilterRecord(raw: unknown): Record<string, ColumnFilterState> | undefined {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, ColumnFilterState> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (
      value !== null &&
      typeof value === 'object' &&
      Array.isArray((value as ColumnFilterState).selected) &&
      typeof (value as ColumnFilterState).emptiness === 'string'
    ) {
      out[key] = value as ColumnFilterState;
    }
  }
  return out;
}

/**
 * The first tab holding rows, in tab order; `deals` when none does or nothing
 * has landed yet. Only consulted until the reader picks a tab themselves.
 */
function firstTabWithRows(state: CompanyDocumentsHistoryState): CompanyDocumentKind {
  for (const kind of COMPANY_DOCUMENT_KINDS) {
    const count = state.counts[kind];
    if (count !== null && count > 0) return kind;
  }
  return 'deals';
}

export function CompanyDocumentsHistory({
  state,
  actions,
}: {
  state: CompanyDocumentsHistoryState;
  /** Rendered right-aligned in the tab strip row — the page passes its create menu. */
  actions?: ReactNode;
}) {
  /*
   * `null` until the reader chooses: the effective tab then follows the data as
   * it lands, so the section opens on the first tab with rows rather than on
   * whichever one happened to be the default while everything was loading.
   */
  // The chosen tab is remembered on the account (owner, 2026-10-16); `null`
  // still means "follow the data".
  const [chosen, setChosen] = useRememberedState<CompanyDocumentKind | null>(
    'company-documents-tab',
    null,
    parseDocumentKind,
  );
  const active = chosen ?? firstTabWithRows(state);

  /*
   * The per-row Actions column — the same Download PDF and "+" controls each
   * list page offers on its own rows, gated exactly as those pages gate them
   * (owner, 2026-10-12). Nothing here re-derives a gate:
   *
   *   - Deals: `CreateFromDealMenu` over `DEAL_CREATE_TARGETS` filtered per
   *     module with `canCreate`, as `/deals` does — an empty list renders no
   *     trigger. Deals have no PDF of their own; `DealQuotesPdfMenu` previews
   *     one of the deal's quotes.
   *   - Quotes: Preview PDF (every stage, `quotes.read`), the download control
   *     (it hides itself when the quote is not in Xero) and `ConvertQuoteMenu` gated on the ROLE through `canConvertQuote()`,
   *     never `/permissions/me` — see that menu's own doc.
   *   - Invoices: the PDF control and the raise-credit-note "+", through the
   *     same `creditNoteGateOf` over the status master read ONCE here, and the
   *     same `useRaiseCreditNote` flow, whose dialogs mount once below.
   *   - Credit notes: the PDF control, plus Submit for approval / Approve
   *     through `useCreditNoteRowActions` (owner, 2026-10-28), whose one
   *     dialog mounts below. *SUPERSEDED: "the PDF control only".*
   */
  const { ability } = useAuth();
  const dealCreateTargets = useMemo(
    () => DEAL_CREATE_TARGETS.filter((target) => ability.canCreate(target.module)),
    [ability],
  );
  const canConvert = ability.canConvertQuote();
  const creditStatuses = useInvoiceStatusFlags();
  const canCreateCreditNote = ability.canCreate('credit-notes');
  const creditDenial = denialReason(ability, 'create', 'credit-notes');
  const raiseCredit = useRaiseCreditNote();
  /*
   * ONE quote PDF viewer for the whole section (it is mounted once on the
   * company page). Preview shows at every quote stage; deal rows pick among the
   * deal's quotes through `DealQuotesPdfMenu`. Mark Sent / Send to Xero in the
   * viewer reload the tables.
   */
  const quotePreview = useQuotePdfPreview({ onChanged: state.reload });
  const creditNoteActions = useCreditNoteRowActions(state.reload);

  const rowActions: RowActions = {
    deals: (deal) => (
      <>
        <DealQuotesPdfMenu
          dealId={deal.id}
          dealName={deal.dealName}
          onPreview={quotePreview.open}
          variant="row"
        />
        {dealCreateTargets.length > 0 && (
          <CreateFromDealMenu deal={deal} targets={dealCreateTargets} />
        )}
      </>
    ),
    quotes: (quote) => (
      <>
        <PreviewQuotePdfButton
          target={{
            id: quote.id,
            quoteNumber: quote.quoteNumber,
            subject: quote.subject,
            companyName: quote.company ? documentCustomerName(quote.company) : null,
          }}
          onPreview={quotePreview.open}
          variant="row"
        />
        <DownloadPdfButton kind="quote" document={quote} reference={quote.subject} variant="row" />
        {canConvert && <ConvertQuoteMenu quote={{ id: quote.id, name: quote.subject }} />}
      </>
    ),
    invoices: (invoice) => (
      <>
        <DownloadPdfButton
          kind="invoice"
          document={invoice}
          reference={invoice.subject}
          variant="row"
        />
        <RaiseCreditNoteButton
          invoice={invoice}
          gate={creditNoteGateOf(
            invoice,
            canCreateCreditNote,
            creditDenial,
            creditStatuses.statuses,
            creditStatuses.failed,
          )}
          variant="row"
          busy={raiseCredit.checkingId === invoice.id}
          onRaise={(row) => raiseCredit.start(row)}
        />
      </>
    ),
    creditNotes: (note) => (
      <>
        <DownloadPdfButton
          kind="credit-note"
          document={note}
          reference={creditNoteRef(note)}
          variant="row"
        />
        {creditNoteActions.controls(note)}
      </>
    ),
  };

  /*
   * ⚠️ **ONE QUICK-EDIT HOST PER KIND, EACH MOUNTED ONCE AROUND THE WHOLE
   * SECTION** — the pattern `InvoiceNumberLink` / `QuoteNumberLink` set (owner,
   * 2026-10-11: a document in this history opens in a modal). A plain click on
   * a name fetches the record and opens its edit form; a modified click, or a
   * reader who may not edit it, is an ordinary navigation to the record page.
   * Each form's `onSaved` is a CLOSE, and the host then calls `state.reload`, so
   * the row reflects the save. Their notices render above the tab strip.
   */
  return (
    <QuoteQuickEdit onSaved={state.reload}>
      <InvoiceQuickEdit onSaved={state.reload}>
        <DealQuickEdit onSaved={state.reload}>
          <CreditNoteQuickEdit onSaved={state.reload}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <DocumentTabs value={active} counts={state.counts} onChange={setChosen} />
                {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
              </div>
              {active === 'creditNotes' && creditNoteActions.notice}
              <DocumentTab kind={active} state={state} rowActions={rowActions} />
            </div>
            {raiseCredit.dialogs}
            {creditNoteActions.dialogs}
            {quotePreview.element}
          </CreditNoteQuickEdit>
        </DealQuickEdit>
      </InvoiceQuickEdit>
    </QuoteQuickEdit>
  );
}

/**
 * The name cell's link look — `TextLink`'s classes restated, because the four
 * quick-edit links are router `<Link>`s of their own and `TextLink` exports no
 * class string. Keep it in step with `TextLink` if that changes.
 */
const NAME_LINK_CLASS =
  'block min-w-0 truncate rounded-sm font-medium text-fg underline-offset-2 decoration-hairline-strong transition-colors duration-150 ease-out hover:underline focus-visible:underline motion-reduce:transition-none';

/**
 * ⚠️ `role="group"` with `aria-pressed`, NOT `role="tab"` — the house toggle,
 * copied from `SettingsPage`'s `SettingsTabs` rather than re-derived: these
 * switch what the table below IS, and a real `tablist` would promise arrow-key
 * navigation between panels that do not share a tabstop. `max-w-full` with
 * `flex-wrap` and NO `shrink-0`, for the reason recorded there: four pills
 * cannot fit 320px, and a box that cannot shrink never wraps.
 *
 * The count beside each label is the server's `meta.total`; a `null` count
 * renders the bare label rather than a `0`.
 */
function DocumentTabs({
  value,
  counts,
  onChange,
}: {
  value: CompanyDocumentKind;
  counts: CompanyDocumentsHistoryState['counts'];
  onChange: (next: CompanyDocumentKind) => void;
}) {
  return (
    <ViewToggle<CompanyDocumentKind>
      label="Sales & Billing"
      value={value}
      onChange={onChange}
      className="max-w-full"
      options={COMPANY_DOCUMENT_KINDS.map((kind) => {
        const count = counts[kind];
        return {
          value: kind,
          label: (
            <>
              {COMPANY_DOCUMENT_LABELS[kind].tab}
              {count !== null && <span className="tabular text-fg-muted">{count}</span>}
            </>
          ),
        };
      })}
    />
  );
}

/** Each kind's per-row Actions cell, built once in `CompanyDocumentsHistory`. */
interface RowActions {
  deals: (deal: Deal) => ReactNode;
  quotes: (quote: Quote) => ReactNode;
  invoices: (invoice: Invoice) => ReactNode;
  creditNotes: (note: CreditNote) => ReactNode;
}

function DocumentTab({
  kind,
  state,
  rowActions,
}: {
  kind: CompanyDocumentKind;
  state: CompanyDocumentsHistoryState;
  rowActions: RowActions;
}) {
  /*
   * `key={kind}` on every table: the three CRM tabs render the same component at
   * the same position, so without it a tab switch would carry one kind's column
   * filters onto another kind's columns.
   */
  switch (kind) {
    case 'deals':
      return (
        <DocumentTable
          key="deals"
          kind="deals"
          cycles={state.cycles}
          actionsOf={rowActions.deals}
          read={state.deals}
          companyId={state.companyId}
          toRow={dealRow}
          nameOf={(deal, row) => (
            <DealNameLink
              dealId={deal.id}
              dealName={row.name}
              className={NAME_LINK_CLASS}
              title={row.name}
            />
          )}
          stateOf={(deal) => <DealStageBadge stage={deal.stage} />}
        />
      );
    case 'quotes':
      return (
        <DocumentTable
          key="quotes"
          kind="quotes"
          cycles={state.cycles}
          actionsOf={rowActions.quotes}
          read={state.quotes}
          companyId={state.companyId}
          toRow={quoteRow}
          nameOf={(quote, row) => (
            <QuoteNumberLink
              quoteId={quote.id}
              quoteNumber={row.name}
              className={NAME_LINK_CLASS}
              title={row.name}
            />
          )}
          stateOf={(quote) => <StageBadge stage={quote.stage} />}
        />
      );
    case 'invoices':
      return (
        <DocumentTable
          key="invoices"
          kind="invoices"
          cycles={state.cycles}
          actionsOf={rowActions.invoices}
          read={state.invoices}
          companyId={state.companyId}
          toRow={invoiceRow}
          nameOf={(invoice, row) => (
            <InvoiceNumberLink
              invoiceId={invoice.id}
              invoiceNumber={row.name}
              className={NAME_LINK_CLASS}
              title={row.name}
            />
          )}
          stateOf={(invoice) => <StatusBadge status={invoice.status} />}
          extra={{
            heading: 'Due',
            filterValue: (invoice) => invoice.dueDate,
            range: { date: true },
            cell: (invoice) => (
              <span className="tabular whitespace-nowrap">{formatDateOnly(invoice.dueDate)}</span>
            ),
          }}
        />
      );
    case 'creditNotes':
      return <CreditNotesTab state={state} actionsOf={rowActions.creditNotes} />;
  }
}

/**
 * The Credit notes tab: the per-currency balance above the rows.
 *
 * Both reads are gated on the same `credit-notes.read` grant, so a 403 on
 * either is a refusal of the whole tab — said once, with no figure and no
 * table, exactly as the standalone panel did. A reader whose refusal is
 * `notOwned` (may read credit notes, not this company's balance) still gets the
 * table; the balance block carries the server's sentence on its own.
 */
function CreditNotesTab({
  state,
  actionsOf,
}: {
  state: CompanyDocumentsHistoryState;
  actionsOf: (note: CreditNote) => ReactNode;
}) {
  const { creditNotes, creditBalances } = state;
  const forbidden =
    creditBalances.kind === 'forbidden'
      ? creditBalances
      : creditNotes.kind === 'forbidden'
        ? creditNotes
        : null;
  if (forbidden) {
    return <CreditRefusal message={forbidden.message} className="py-2" />;
  }
  return (
    <div className="flex flex-col gap-4">
      <CompanyCreditBalance
        balances={creditBalances}
        notesTotal={creditNotes.kind === 'ok' ? creditNotes.data.total : null}
      />
      <DocumentTable
        key="creditNotes"
        kind="creditNotes"
        cycles={state.cycles}
        actionsOf={actionsOf}
        read={creditNotes}
        companyId={state.companyId}
        toRow={creditNoteRow}
        nameOf={(note, row) => (
          <CreditNoteLink
            creditNote={note}
            label={row.name}
            className={NAME_LINK_CLASS}
            title={row.name}
          />
        )}
        stateOf={(note) => (
          <Badge tone={creditNoteStatusTone(note.status)}>{note.status.name}</Badge>
        )}
        extra={{
          heading: CREDIT_REMAINING_LABEL,
          numeric: true,
          filterValue: (note) => note.remainingAmount,
          range: { decimal: true },
          /*
           * ⚠️ `remainingAmount` is reported even on a DRAFT, where it is the
           * note's whole value and can be put against nothing; the figure alone
           * would read as available credit, so the badge sits beside it.
           */
          cell: (note) => (
            <span className="inline-flex flex-wrap items-center justify-end gap-1.5 tabular">
              <Money
                amount={note.remainingAmount}
                currency={note.currency}
                className="whitespace-nowrap"
              />
              {!spendableOf(note) && (
                <Badge tone="neutral" variant="outline">
                  Not spendable
                </Badge>
              )}
            </span>
          ),
        }}
      />
    </div>
  );
}

/* --- one kind's table ----------------------------------------------------- */

interface ExtraColumn<T> {
  heading: string;
  numeric?: boolean;
  cell: (record: T) => ReactNode;
  /** The value the column's funnel filters on — a date or a decimal string. */
  filterValue: (record: T) => string | null;
  /** The funnel's bound shape: `{ date: true }` or `{ decimal: true }`. */
  range: ColumnRangeSpec;
}

/** One loaded record with everything its row and its filters read. */
interface Entry<T> {
  readonly record: T;
  readonly row: CompanyDocumentRow;
  readonly cycle: CycleOfDate | null;
}

/** A filterable column over the loaded entries. */
interface FilterColumn<T> {
  readonly key: string;
  readonly label: string;
  readonly numeric?: boolean;
  /** The cell's filterable value; `null` or blank counts as EMPTY. */
  readonly get: (entry: Entry<T>) => string | null;
  /** Present = an inclusive bound pair instead of a value list. */
  readonly range?: ColumnRangeSpec;
  /** A value's label in the list; defaults to the value. */
  readonly format?: (value: string) => string;
  /** Orders the value list; defaults to label order. */
  readonly compare?: (a: ColumnFilterOption, b: ColumnFilterOption) => number;
}

function byLabel(a: ColumnFilterOption, b: ColumnFilterOption): number {
  return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
}

function cellOf<T>(column: FilterColumn<T>, entry: Entry<T>): string | null {
  const raw = column.get(entry);
  return raw === null || raw.trim() === '' ? null : raw;
}

/**
 * Does one entry pass one column's filter? Client-side over the loaded rows,
 * which is the whole of this tab. Emptiness first; then either the inclusive
 * bounds (`rangeBounds` is the single gate that drops an empty or malformed
 * box) or the ticked values.
 */
function passes<T>(
  column: FilterColumn<T>,
  state: ColumnFilterState | undefined,
  entry: Entry<T>,
): boolean {
  if (!state) return true;
  const cell = cellOf(column, entry);
  if (state.emptiness === 'empty' && cell !== null) return false;
  if (state.emptiness === 'notEmpty' && cell === null) return false;
  if (column.range) {
    return withinBounds(
      cell,
      rangeBounds(state.range, column.range),
      column.range.date === true ? 'date' : 'decimal',
    );
  }
  if (state.selected.length === 0) return true;
  return cell !== null && state.selected.includes(cell);
}

function optionsOf<T>(column: FilterColumn<T>, entries: readonly Entry<T>[]): ColumnFilterOption[] {
  if (column.range) return [];
  const counts = new Map<string, number>();
  for (const entry of entries) {
    const cell = cellOf(column, entry);
    if (cell === null) continue;
    counts.set(cell, (counts.get(cell) ?? 0) + 1);
  }
  return Array.from(counts, ([value, count]) => ({
    value,
    label: column.format?.(value) ?? value,
    count,
  })).sort(column.compare ?? byLabel);
}

/**
 * One kind's table, over the shared `CompanyDocumentRow` plus a per-kind badge
 * and an optional per-kind extra column. Three states — skeleton rows in the
 * real shape, an empty state stating a fact, a failure line naming what failed
 * — and the `View all` link under a populated table.
 */
function DocumentTable<T extends { id: number }>({
  kind,
  cycles,
  read,
  companyId,
  toRow,
  nameOf,
  stateOf,
  extra,
  actionsOf,
}: {
  kind: CompanyDocumentKind;
  /** For the derived Cycle column; `null` renders every cell as `—`. */
  cycles: readonly Cycle[] | null;
  /**
   * The row's Actions cell. Pinned to the right edge, as every list page pins
   * its actions, so it stays in view while the table scrolls at 320px.
   */
  actionsOf: (record: T) => ReactNode;
  read: CreditRead<DocumentPage<T>>;
  companyId: number;
  toRow: (record: T) => CompanyDocumentRow;
  /** The name cell's link — a quick-edit link that opens the record in a modal. */
  nameOf: (record: T, row: CompanyDocumentRow) => ReactNode;
  stateOf: (record: T) => ReactNode;
  extra?: ExtraColumn<T>;
}) {
  const labels = COMPANY_DOCUMENT_LABELS[kind];
  // Column funnels are remembered per tab on the account (owner, 2026-10-16).
  const [filters, setFilters] = useRememberedState<Record<string, ColumnFilterState>>(
    `company-documents-${kind}-filters`,
    {},
    parseFilterRecord,
  );

  const loadedRows = read.kind === 'ok' ? read.data.rows : null;
  const entries = useMemo<Entry<T>[]>(() => {
    if (loadedRows === null) return [];
    return loadedRows.map((record) => {
      const row = toRow(record);
      return { record, row, cycle: cycleOfDate(row.cycleDate, cycles) };
    });
  }, [loadedRows, toRow, cycles]);

  // Newest cycle first in the Cycle list — by start date, never by id.
  const cycleStart = new Map((cycles ?? []).map((cycle) => [String(cycle.id), cycle.startDate]));
  const cycleName = new Map((cycles ?? []).map((cycle) => [String(cycle.id), cycle.cycleName]));
  const stateName = new Map(
    entries.flatMap((entry) =>
      entry.row.state === null ? [] : [[String(entry.row.state.id), entry.row.state.name] as const],
    ),
  );

  const columns: FilterColumn<T>[] = [
    {
      key: 'cycle',
      label: 'Cycle',
      get: (entry) => (entry.cycle === null ? null : String(entry.cycle.id)),
      format: (value) => cycleName.get(value) ?? value,
      compare: (a, b) => (cycleStart.get(b.value) ?? '').localeCompare(cycleStart.get(a.value) ?? ''),
    },
    { key: 'name', label: labels.nameHeading, get: (entry) => entry.row.name },
    {
      key: 'state',
      label: labels.stateHeading,
      get: (entry) => (entry.row.state === null ? null : String(entry.row.state.id)),
      format: (value) => stateName.get(value) ?? value,
    },
    {
      key: 'amount',
      label: labels.amountHeading,
      numeric: true,
      get: (entry) => entry.row.amount,
      range: { decimal: true, signed: true },
    },
    {
      key: 'date',
      label: labels.dateHeading,
      get: (entry) => entry.row.date,
      range: { date: true },
    },
  ];
  if (extra) {
    columns.push({
      key: 'extra',
      label: extra.heading,
      numeric: extra.numeric,
      get: (entry) => extra.filterValue(entry.record),
      range: extra.range,
    });
  }

  const visible = entries.filter((entry) =>
    columns.every((column) => passes(column, filters[column.key], entry)),
  );
  const activeCount = columns.filter((column) => {
    const value = filters[column.key];
    return value !== undefined && isFilterActive(value);
  }).length;

  const headerFor = (column: FilterColumn<T>): FilterableTHProps => ({
    label: column.label,
    numeric: column.numeric,
    filter: {
      label: column.label,
      options: optionsOf(column, entries),
      value: filters[column.key] ?? EMPTY_FILTER,
      range: column.range,
      scope: 'page',
      pageRowCount: entries.length,
      disabled: read.kind !== 'ok',
      onChange: (next) =>
        setFilters((current) => {
          if (hasFilterInput(next)) return { ...current, [column.key]: next };
          if (!(column.key in current)) return current;
          const rest: Record<string, ColumnFilterState> = {};
          for (const [key, value] of Object.entries(current)) {
            if (key !== column.key) rest[key] = value;
          }
          return rest;
        }),
    },
  });

  switch (read.kind) {
    case 'forbidden':
      // Unreachable for the three open kinds today; a grant can still change
      // under a live token, so the sentence is the server's rather than ours.
      return (
        <p role="status" className="m-0 flex flex-col gap-0.5 text-sm text-fg-secondary">
          <span className="font-semibold text-fg">
            Your account is not allowed to read {labels.plural}.
          </span>
          <span className="min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
            {read.message}
          </span>
        </p>
      );
    case 'notOwned':
    case 'error':
      return (
        <p role="alert" className="m-0 flex flex-col gap-0.5 text-sm text-danger">
          <span className="font-semibold">Couldn’t load {labels.plural}.</span>
          <span className="min-w-0 text-xs [overflow-wrap:anywhere]">{read.message}</span>
        </p>
      );
    case 'ok':
      if (read.data.rows.length === 0) {
        return <EmptyState title={labels.emptyTitle} description={labels.emptyDescription} />;
      }
      break;
    case 'loading':
      break;
  }

  const headers = (
    <THead>
      <TR>
        {columns.map((column) => (
          <FilterableTH key={column.key} {...headerFor(column)} />
        ))}
        <TH pinned className="text-right">
          Actions
        </TH>
      </TR>
    </THead>
  );

  if (read.kind === 'loading') {
    return (
      <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
        <Table className="min-w-[50rem]">
          {headers}
          <TBody>
            {Array.from({ length: 3 }, (_, index) => (
              <TR key={index}>
                <TD>
                  <Skeleton className="h-4 w-16" />
                </TD>
                <TD>
                  <Skeleton className="h-4 w-48" />
                </TD>
                <TD>
                  <Skeleton className="h-4 w-20" />
                </TD>
                <TD numeric>
                  <Skeleton className="ml-auto h-4 w-24" />
                </TD>
                <TD>
                  <Skeleton className="h-4 w-20" />
                </TD>
                {extra && (
                  <TD numeric={extra.numeric}>
                    <Skeleton className={cx('h-4 w-20', extra.numeric && 'ml-auto')} />
                  </TD>
                )}
                <TD pinned>
                  <Skeleton className="ml-auto h-4 w-14" />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
    );
  }

  const { total } = read.data;
  return (
    <div className="flex flex-col gap-2">
      <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
        <Table className="min-w-[50rem]">
          {headers}
          <TBody>
            {visible.map(({ record, row, cycle }) => {
              return (
                <TR key={row.id} interactive>
                  <TD className="whitespace-nowrap">{orDash(cycle?.name)}</TD>
                  <TD className="max-w-[20rem]">
                    {nameOf(record, row)}
                    {row.reference !== null && (
                      <span className="block truncate text-xs text-fg-muted" title={row.reference}>
                        {row.reference}
                      </span>
                    )}
                  </TD>
                  <TD>{stateOf(record)}</TD>
                  <TD numeric className="tabular whitespace-nowrap">
                    <Money amount={row.amount} currency={orDash(row.currency)} />
                  </TD>
                  <TD className="tabular whitespace-nowrap">{formatDateOnly(row.date)}</TD>
                  {extra && <TD numeric={extra.numeric}>{extra.cell(record)}</TD>}
                  <TD pinned className="whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5">
                      {actionsOf(record)}
                    </div>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </TableWrap>
      {visible.length === 0 && activeCount > 0 && (
        <EmptyState
          title="No rows match"
          description={`No loaded ${labels.plural} match these filters.`}
          action={
            <Button variant="secondary" size="sm" onClick={() => setFilters({})}>
              Clear filters
            </Button>
          }
        />
      )}
      {total > 0 && (
        <TextLink
          to={companyDocumentListHref(kind, companyId)}
          className="self-start text-sm font-medium"
        >
          {viewAllLabel(kind, total)}
        </TextLink>
      )}
    </div>
  );
}
